import React from "react";
import { Graphics } from "@pixi/react";
import { COMBAT_FLASH_DURATION } from "./constants";

// Troop density heatmap overlay — renders density as colored cells on owned territory.
// Memoized to avoid re-draws when data hasn't meaningfully changed.
export const TroopDensityHeatmap = React.memo(({ densityMap, cellSize, visibleBounds }) => {
  if (!densityMap || densityMap.length < 3) return null;

  return (
    <Graphics
      zIndex={105}
      draw={(g) => {
        g.clear();
        const minX = visibleBounds?.minX ?? -Infinity;
        const maxX = visibleBounds?.maxX ?? Infinity;
        const minY = visibleBounds?.minY ?? -Infinity;
        const maxY = visibleBounds?.maxY ?? Infinity;

        for (let i = 0; i < densityMap.length; i += 3) {
          const x = densityMap[i];
          const y = densityMap[i + 1];
          const density = densityMap[i + 2]; // 0-255 quantized

          if (x < minX || x > maxX || y < minY || y > maxY) continue;
          if (density <= 0) continue;

          const t = Math.min(1, density / 255);
          // Color gradient: blue (low) -> cyan (mid-low) -> green (mid) -> yellow (mid-high) -> red (high)
          let r, gVal, b;
          if (t < 0.25) {
            // Blue -> Cyan
            const s = t * 4;
            r = 0;
            gVal = Math.round(s * 200);
            b = 180 + Math.round(s * 75);
          } else if (t < 0.5) {
            // Cyan -> Green
            const s = (t - 0.25) * 4;
            r = 0;
            gVal = 200 + Math.round(s * 55);
            b = Math.round((1 - s) * 255);
          } else if (t < 0.75) {
            // Green -> Yellow
            const s = (t - 0.5) * 4;
            r = Math.round(s * 255);
            gVal = 255;
            b = 0;
          } else {
            // Yellow -> Red
            const s = (t - 0.75) * 4;
            r = 255;
            gVal = Math.round((1 - s) * 255);
            b = 0;
          }
          const color = (r << 16) | (gVal << 8) | b;
          // Higher alpha so density is always visible: 0.20 (low) to 0.55 (high)
          const alpha = 0.20 + t * 0.35;

          g.beginFill(color, alpha);
          g.drawRect(x * cellSize, y * cellSize, cellSize, cellSize);
          g.endFill();
        }
      }}
    />
  );
}, (prev, next) => {
  // Custom comparison: skip re-render if densityMap data is identical
  if (prev.cellSize !== next.cellSize) return false;
  if (prev.visibleBounds !== next.visibleBounds) return false;
  const a = prev.densityMap;
  const b = next.densityMap;
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  // Sample comparison — check every 3rd triple for changes
  const step = Math.max(3, Math.floor(a.length / 30) * 3);
  for (let i = 0; i < a.length; i += step) {
    if (a[i] !== b[i] || a[i + 1] !== b[i + 1] || a[i + 2] !== b[i + 2]) return false;
  }
  return true;
});

// Border pressure visualization — border cells glow green (strong) to red (thin) based on troop density
export const BorderPressureOverlay = React.memo(({ playerNation, densityMap, cellSize, visibleBounds }) => {
  if (!playerNation || !densityMap || densityMap.length < 3) return null;

  const territory = playerNation.territory;
  if (!territory?.x?.length) return null;

  return (
    <Graphics
      zIndex={107}
      draw={(g) => {
        g.clear();

        // Build density lookup from flat [x,y,density,...] array
        const densityLookup = new Map();
        for (let i = 0; i < densityMap.length; i += 3) {
          densityLookup.set((densityMap[i + 1] << 16) | densityMap[i], densityMap[i + 2]);
        }

        // Build territory set
        const terrSet = new Set();
        for (let i = 0; i < territory.x.length; i++) {
          terrSet.add((territory.y[i] << 16) | territory.x[i]);
        }

        // Find border cells and compute average density
        const borderCells = [];
        let totalDensity = 0;
        let densityCount = 0;

        for (let i = 0; i < territory.x.length; i++) {
          const x = territory.x[i];
          const y = territory.y[i];

          // Visibility culling
          if (visibleBounds) {
            if (x < visibleBounds.minX || x > visibleBounds.maxX ||
                y < visibleBounds.minY || y > visibleBounds.maxY) continue;
          }

          // Check if border cell (has non-owned neighbor)
          const isBorder =
            !terrSet.has(((y - 1) << 16) | x) ||
            !terrSet.has((y << 16) | (x + 1)) ||
            !terrSet.has(((y + 1) << 16) | x) ||
            !terrSet.has((y << 16) | (x - 1));
          if (!isBorder) continue;

          const d = densityLookup.get((y << 16) | x) || 0;
          borderCells.push({ x, y, density: d });
          totalDensity += d;
          densityCount++;
        }

        if (borderCells.length === 0) return;
        const avgDensity = Math.max(1, totalDensity / densityCount);

        for (const cell of borderCells) {
          // t: 0 = red (thin/no troops), 0.5 = yellow (average), 1 = green (strong)
          const t = Math.min(1, Math.max(0, cell.density / (avgDensity * 2)));

          let r, gVal, b;
          if (t < 0.5) {
            const s = t * 2;
            r = 255;
            gVal = Math.round(s * 200);
            b = 0;
          } else {
            const s = (t - 0.5) * 2;
            r = Math.round((1 - s) * 255);
            gVal = 200 + Math.round(s * 55);
            b = 0;
          }
          const color = (r << 16) | (gVal << 8) | b;
          // Higher alpha so border defense is clearly visible
          const alpha = 0.35 + Math.abs(t - 0.5) * 0.3;

          g.beginFill(color, alpha);
          g.drawRect(cell.x * cellSize, cell.y * cellSize, cellSize, cellSize);
          g.endFill();
        }
      }}
    />
  );
}, (prev, next) => {
  if (prev.cellSize !== next.cellSize) return false;
  if (prev.playerNation !== next.playerNation) return false;
  if (prev.visibleBounds !== next.visibleBounds) return false;
  const a = prev.densityMap;
  const b = next.densityMap;
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const step = Math.max(3, Math.floor(a.length / 30) * 3);
  for (let i = 0; i < a.length; i += step) {
    if (a[i] !== b[i] || a[i + 1] !== b[i + 1] || a[i + 2] !== b[i + 2]) return false;
  }
  return true;
});

// Combat flash effects at cells where territory changes hands
export const CombatFlashLayer = React.memo(({ combatFlashes, cellSize, animTime, visibleBounds }) => {
  if (!combatFlashes || combatFlashes.length === 0) return null;

  return (
    <Graphics
      zIndex={108}
      draw={(g) => {
        g.clear();
        const now = animTime || performance.now();
        for (const flash of combatFlashes) {
          const age = now - flash.createdAt;
          if (age < 0 || age > COMBAT_FLASH_DURATION) continue;

          // Visibility culling
          if (visibleBounds) {
            if (flash.x < visibleBounds.minX || flash.x > visibleBounds.maxX ||
                flash.y < visibleBounds.minY || flash.y > visibleBounds.maxY) continue;
          }

          const t = age / COMBAT_FLASH_DURATION;
          // Quick fade-in (first 15%), then fade-out
          let alpha;
          if (t < 0.15) {
            alpha = (t / 0.15) * 0.7;
          } else {
            alpha = 0.7 * (1 - (t - 0.15) / 0.85);
          }

          const color = flash.type === "capture" ? 0x44ff44 : 0xff4444;
          const size = cellSize * 1.2;
          const offset = (size - cellSize) / 2;

          g.beginFill(color, Math.max(0, alpha));
          g.drawRect(
            flash.x * cellSize - offset,
            flash.y * cellSize - offset,
            size,
            size
          );
          g.endFill();
        }
      }}
    />
  );
});
